import { Component, forwardRef, OnInit } from '@angular/core';
import { UntypedFormArray, Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PrFormService } from '@app/components/pr-form/pr-form.service';
import { AssetDao } from '@app/dao/asset/asset.dao';
import { EstateDao } from '@app/dao/estate/estate.dao';
import { LoanDao } from '@app/dao/loan/loan.dao';
import { extractLoanApiErrorMessages } from '@app/services/loan/loan-api-error.utils';
import {
  neutralizeLoanPaliers,
  refreshLoanDiffereValidity,
  refreshLoanPaliersValidity,
} from '@app/services/loan/loan-palier.form';
import {
  hasLoanPaliers,
  toPositiveNumber,
} from '@app/services/loan/loan-palier.utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { EaLoanFormBuilder } from './ea-loan-form.builder';

const MONTHLY_PAYMENT_REPLACED_MESSAGE =
  'La mensualité saisie est écartée : un crédit à paliers définit sa mensualité palier par palier, à partir du montant emprunté.';

const PALIERS_DISCARDED_MESSAGE =
  'Les paliers de ce crédit seront supprimés à l’enregistrement.';

const PALIERS_UNSUPPORTED_MESSAGE =
  'Les paliers de ce crédit seront supprimés à l’enregistrement : le mode simple et le type In Fine ne les gèrent pas.';

@UntilDestroy()
@Component({
  selector: 'ea-loan-form',
  templateUrl: './ea-loan-form.component.html',
  styleUrls: ['./ea-loan-form.component.scss'],
  providers: [
    {
      provide: PrFormAbstract,
      useExisting: forwardRef(() => EaLoanFormComponent),
    },
  ],
  standalone: false,
})
export class EaLoanFormComponent extends EaLoanFormBuilder implements OnInit {
  investments: any[] = [];
  loanCategories: any[] = [];
  loanTypes: any[] = [];
  providers: any[] = [];
  agencesBancaires: any[] = [];
  agencesBancairesFiltered: any[] = [];
  submitErrorMessages: string[] = [];
  paliersReplacedMonthlyPayment = false;
  doneSubject: Subject<void> = new Subject<void>();

  constructor(
    protected formConstructor: PrFormConstructor,
    private estateDao: EstateDao,
    private loanDao: LoanDao,
    private service: PrFormService,
    private assetDao: AssetDao,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    combineLatest([
      this.estateDao.getEstates(),
      this.loanDao.getReferences(),
      this.assetDao.getProviders({
        productType: 'LOAN',
        size: -1,
        page: 1,
      }),
      this.assetDao.getAgencesBancaires(),
    ]).subscribe(([investments, references, providers, agencesBancaires]) => {
      this.providers = providers.filter(provider => provider.externalId);
      this.agencesBancaires = agencesBancaires;

      const providerId = this.form.get('providerId').value;
      if (providerId) {
        this.agencesBancairesFiltered = agencesBancaires.filter(
          agence => agence.providerId === providerId,
        );
      } else {
        this.agencesBancairesFiltered = agencesBancaires;
      }

      _.forEach(investments.data.groupedRentalList, group => {
        _.forEach(group.list, item => {
          this.investments.push({ label: item.label, id: item.investmentId });
        });
      });

      _.forEach(investments.data.groupedPersonalUseList, group => {
        _.forEach(group.list, item => {
          this.investments.push({ label: item.label, id: item.investmentId });
        });
      });

      this.loanCategories = references.categories;
      this.loanTypes = references.types;
    });
    // to be reviewed to optimize
    this.form.get('amountBorrowed').setValidators([Validators.required]);
    this.form.get('simuChoice').valueChanges.subscribe((value: boolean) => {
      if (value === true) {
        this.form.get('amountBorrowed').setValidators([Validators.required]);
        this.form.get('amountMonthly').clearValidators();
      } else {
        this.form.get('amountMonthly').setValidators([Validators.required]);
        this.form.get('amountBorrowed').clearValidators();
      }
      this.form.get('amountMonthly').updateValueAndValidity();
      this.form.get('amountBorrowed').updateValueAndValidity();
    });
    this.form.get('mode').valueChanges.subscribe((value: boolean) => {
      this.applyAdvancedValidators(value === true);
    });
    // `valueChanges` ne se déclenche pas à l'init : on applique selon le mode courant.
    this.applyAdvancedValidators(this.form.get('mode').value === true);

    this.form
      .get('type')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => this.applyPaliersAvailability());
    this.form
      .get('paliersEnabled')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(enabled => {
        this.paliersReplacedMonthlyPayment =
          enabled === true && this.hasMonthlyPaymentInput;
        if (enabled === true) this.form.get('simuChoice')?.setValue(true);
        refreshLoanDiffereValidity(this.form);
      });
  }

  get isPaliersAvailable(): boolean {
    return (
      this.form.get('mode')?.value === false &&
      (this.form.get('type')?.value || 'FIX') === 'FIX'
    );
  }

  get hasPaliers(): boolean {
    return this.form.get('paliersEnabled')?.value === true;
  }

  get paliersNoticeMessage(): string | null {
    if (this.hasPaliers) {
      return this.paliersReplacedMonthlyPayment
        ? MONTHLY_PAYMENT_REPLACED_MESSAGE
        : null;
    }

    if (!hasLoanPaliers(this.entity)) {
      return null;
    }

    return this.isPaliersAvailable
      ? PALIERS_DISCARDED_MESSAGE
      : PALIERS_UNSUPPORTED_MESSAGE;
  }

  private get hasMonthlyPaymentInput(): boolean {
    return (
      this.form.get('simuChoice')?.value === false &&
      toPositiveNumber(this.form.get('amountMonthly')?.value) !== null
    );
  }

  private applyPaliersAvailability() {
    if (this.isPaliersAvailable) {
      refreshLoanPaliersValidity(this.form);
      return;
    }
    neutralizeLoanPaliers(this.form);
  }

  /**
   * Active/désactive les validateurs des champs du mode avancé (type
   * d'assurance, quotités). En mode simple ces sections sont masquées : on
   * retire leurs validateurs — y compris ceux des contrôles internes du
   * FormArray `insuranceQuotite` (`ownerId`/`quotite`) — sinon le formulaire
   * reste invalide de façon invisible et la sauvegarde est bloquée sans message.
   * `updateValueAndValidity` est requis pour recalculer l'état des contrôles.
   */
  private applyAdvancedValidators(simple: boolean) {
    refreshLoanDiffereValidity(this.form);

    const isFix = this.form.get('type')?.value === 'FIX';
    const insuranceType = this.form.get('insuranceType');
    if (insuranceType) {
      if (!simple && isFix) insuranceType.setValidators([Validators.required]);
      else insuranceType.clearValidators();
      insuranceType.updateValueAndValidity({ emitEvent: false });
    }

    const quotites = this.form.get('insuranceQuotite') as UntypedFormArray;
    quotites.controls.forEach(group => {
      const ownerId = group.get('ownerId');
      const quotite = group.get('quotite');
      if (simple) {
        ownerId?.clearValidators();
        quotite?.clearValidators();
      } else {
        ownerId?.setValidators([Validators.required]);
        quotite?.setValidators([
          Validators.required,
          Validators.max(100),
          Validators.min(0),
        ]);
      }
      ownerId?.updateValueAndValidity({ emitEvent: false });
      quotite?.updateValueAndValidity({ emitEvent: false });
    });
    if (simple) quotites.clearValidators();
    else quotites.setValidators([Validators.required]);
    quotites.updateValueAndValidity({ emitEvent: false });

    this.applyPaliersAvailability();
  }

  get title() {
    if (!this.entity || !this.entity.id) return 'Ajouter un nouveau crédit';
    if (!this.entity.label || this.entity.label === '')
      return 'Modifier le crédit';
    return 'Modifier le crédit “' + this.entity.label + '”';
  }

  onSubmit() {
    this.prFormService.loading();
    this.submitErrorMessages = [];
    this.loanDao.saveLoan(this.extract()).subscribe({
      next: success => {
        this.prFormService.loaded();
        this.doneSubject.next();
        if (this.callback) this.callback(success);
        this.notificationService.success('La sauvegarde a réussi');
      },
      error: errors => {
        console.error(errors);
        this.prFormService.loaded();
        this.submitErrorMessages = extractLoanApiErrorMessages(errors);
        this.notificationService.error('La sauvegarde a échoué');
      },
    });
  }
  onCancel() {
    this.service.close();
  }

  get insuranceQuotite(): UntypedFormArray {
    return this.form.get('insuranceQuotite') as UntypedFormArray;
  }

  addInsuranceQuotite(): void {
    const group = this.formBuilder.group({
      ownerId: [null, Validators.required],
      quotite: [100, [Validators.required, Validators.max(100)]],
    });
    this.insuranceQuotite.push(group);
  }

  get canRemoveInsuranceQuotite(): boolean {
    return this.insuranceQuotite.length > 1;
  }

  removeInsuranceQuotite(index: number): void {
    if (!this.canRemoveInsuranceQuotite) return;
    this.insuranceQuotite.removeAt(index);
  }

  onBanqueChange(event) {
    this.agencesBancairesFiltered = this.agencesBancaires.filter(
      agence => agence.providerId === event.value,
    );
    this.form.get('agenceBancaireId').setValue(null);
    this.form.get('agenceBancaireId').updateValueAndValidity();
  }
}
